import { useCallback, useEffect, useRef, useState } from 'react'
import {
  habitAdjustmentService,
  type HabitAdjustmentCheckin,
  type HabitAdjustmentRevisitDueItem,
} from '../services/habitAdjustmentService'

export function useHabitAdjustmentCheckin(pollIntervalMs: number = 60000) {
  const [checkin, setCheckin] = useState<HabitAdjustmentCheckin | null>(null)
  const [checkins, setCheckins] = useState<HabitAdjustmentCheckin[]>([])
  const [revisitDue, setRevisitDue] = useState<HabitAdjustmentRevisitDueItem[]>([])
  const shownIds = useRef<Set<string>>(new Set())

  const refetch = useCallback(async () => {
    try {
      const [checkinResp, revisitResp] = await Promise.all([
        habitAdjustmentService.getCheckin('today'),
        habitAdjustmentService.getRevisitDue('today'),
      ])
      const nextCheckins = checkinResp.checkins || (checkinResp.checkin ? [checkinResp.checkin] : [])
      setCheckins(nextCheckins)
      setCheckin(checkinResp.checkin || nextCheckins[0] || null)
      setRevisitDue(revisitResp.revisit_due || [])
    } catch (error) {
      console.warn('Failed to fetch habit adjustment state', error)
    }
  }, [])

  useEffect(() => {
    void refetch()
    const interval = window.setInterval(() => void refetch(), pollIntervalMs)
    return () => window.clearInterval(interval)
  }, [pollIntervalMs, refetch])

  const markShown = useCallback(async (checkinId: string) => {
    if (shownIds.current.has(checkinId)) return
    shownIds.current.add(checkinId)
    try {
      await habitAdjustmentService.markShown(checkinId)
    } catch (error) {
      console.warn('Failed to mark habit adjustment checkin as shown', error)
    }
  }, [])

  return {
    checkin,
    checkins,
    revisitDue,
    refetch,
    markShown,
    respond: habitAdjustmentService.respond,
    diagnose: habitAdjustmentService.diagnose,
    chooseAction: habitAdjustmentService.chooseAction,
    confirm: habitAdjustmentService.confirm,
    pause: habitAdjustmentService.pause,
    restart: habitAdjustmentService.restart,
    archive: habitAdjustmentService.archive,
  }
}

export function useHabitAdjustmentCheckinForHabit(habitDefId?: number, pollIntervalMs: number = 60000) {
  const [checkin, setCheckin] = useState<HabitAdjustmentCheckin | null>(null)
  const shownIds = useRef<Set<string>>(new Set())

  const refetch = useCallback(async () => {
    if (!habitDefId) {
      setCheckin(null)
      return
    }
    try {
      const response = await habitAdjustmentService.getCheckin('habit_detail', habitDefId)
      setCheckin(response.checkin)
    } catch (error) {
      console.warn('Failed to fetch habit detail checkin', error)
    }
  }, [habitDefId])

  useEffect(() => {
    void refetch()
    const interval = window.setInterval(() => void refetch(), pollIntervalMs)
    return () => window.clearInterval(interval)
  }, [pollIntervalMs, refetch])

  const markShown = useCallback(async (checkinId: string) => {
    if (shownIds.current.has(checkinId)) return
    shownIds.current.add(checkinId)
    try {
      await habitAdjustmentService.markShown(checkinId)
    } catch (error) {
      console.warn('Failed to mark habit detail checkin as shown', error)
    }
  }, [])

  return {
    checkin,
    refetch,
    markShown,
    respond: habitAdjustmentService.respond,
    diagnose: habitAdjustmentService.diagnose,
    chooseAction: habitAdjustmentService.chooseAction,
    confirm: habitAdjustmentService.confirm,
    pause: habitAdjustmentService.pause,
    restart: habitAdjustmentService.restart,
    archive: habitAdjustmentService.archive,
  }
}
